// GET /api/firm/deadlines - the agenda feed of the viewer's scope (the firm
// for admin+): explicit deadlines plus hearings (calendar links,
// hearing-type deadlines, nextHearing), deduped per case and day
// server-side. No fixture fallback: the calendar owns honest empty and
// error states instead.

import { getAuthHeaders } from "./auth";

export interface AgendaItem {
  caseId: string;
  eId?: string;
  clientName: string;
  type:
    | "filing"
    | "hearing"
    | "interview"
    | "response"
    | "statute"
    | "biometrics"
    | "ead"
    | "custom";
  label: string;
  dueDate: string; // ISO
  daysAway: number;
  severity: "critical" | "urgent" | "caution" | "safe";
  location?: string;
  // A day has no time to print; an instant does.
  allDay: boolean;
}

export interface AgendaResult {
  deadlines: AgendaItem[];
}

// One window wide enough for the 7-day strip plus a "next up" pointer
// when the week is empty.
export const AGENDA_DAYS = 90;

/** A hearing or an interview is an event the firm attends; a row of any
    other type is owed, and a missed one is overdue, not merely past. */
export function isDeadlineEvent(type?: string): boolean {
  return type === "hearing" || type === "interview";
}

/** An event that went by: it took place, so it is neither owed nor the
    row that matters next. */
export function isPastEvent(d: { days: number; type?: string }): boolean {
  return d.days < 0 && isDeadlineEvent(d.type);
}

// Day-key contract shared by every consumer of this feed (/calendar, the
// deadline radar), so two screens can never place one item on two days.
// Strips key their columns by LOCAL date parts; how an item picks its
// column depends on whether its dueDate is a real instant.

export function localKey(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// The server says which items are days. Without the flag, a day stored as
// midnight UTC is told by that sentinel - it hides the time; anything else
// is a real instant (a linked calendar event) and both its time and its
// day belong to the viewer's zone, not to the UTC slice.
export function isDateOnly(iso: string, allDay?: boolean): boolean {
  return allDay ?? iso.slice(11, 16) === "00:00";
}

export function itemDayKey(iso: string, allDay?: boolean): string {
  return isDateOnly(iso, allDay) ? iso.slice(0, 10) : localKey(new Date(iso));
}

// Calendar days to a deadline's day, negative once it has passed. Instant
// arithmetic told a paralegal at 9pm that a deadline due that same day had
// closed yesterday, because the stored midnight-UTC sentinel had already
// rolled over. A caller whose value is a real instant says so, or one
// that happens to fall on UTC midnight is read as a day.
export function daysUntilDay(iso: string, allDay?: boolean): number {
  const dayNoon = new Date(`${itemDayKey(iso, allDay)}T12:00:00`);
  const todayNoon = new Date(`${localKey(new Date())}T12:00:00`);
  return Math.round((dayNoon.getTime() - todayNoon.getTime()) / 864e5);
}

export async function fetchFirmAgenda(): Promise<AgendaResult> {
  const res = await fetch(`/api/firm/deadlines?days=${AGENDA_DAYS}`, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`GET /api/firm/deadlines failed: ${res.status}`);
  return (await res.json()) as AgendaResult;
}
